import {
  FaReact,
  FaCss3Alt,
  FaHtml5,
  FaJs,
  FaGitAlt,
} from "react-icons/fa";
import {
  SiRedux,
  SiTailwindcss,
  SiNextdotjs,
  SiFramer,
  SiTypescript,
  SiVuedotjs,
  SiDotnet,
  SiSharp,
} from "react-icons/si";
import { Reveal, MaskText } from "./ui/motion";
import Marquee from "./Marquee";
import { Panel, Parallax } from "../journey/Panel";

const TECH = [
  { icon: <FaJs />, name: "JavaScript" },
  { icon: <SiTypescript />, name: "TypeScript" },
  { icon: <FaReact />, name: "React" },
  { icon: <SiVuedotjs />, name: "Vue" },
  { icon: <SiNextdotjs />, name: "Next.js" },
  { icon: <SiRedux />, name: "Redux" },
  { icon: <SiTailwindcss />, name: "Tailwind" },
  { icon: <SiFramer />, name: "Framer" },
  { icon: <FaHtml5 />, name: "HTML5" },
  { icon: <FaCss3Alt />, name: "CSS" },
  { icon: <SiSharp />, name: "C#" },
  { icon: <SiDotnet />, name: ".NET" },
  { icon: <FaGitAlt />, name: "Git" },
];

/* Skills grouped by discipline — one card per group. */
const SKILL_GROUPS = [
  {
    no: "01",
    title: "Languages",
    skills: [
      { icon: <FaJs />, name: "JavaScript", tag: "Language" },
      { icon: <SiTypescript />, name: "TypeScript", tag: "Language" },
      { icon: <SiSharp />, name: "C#", tag: "Language" },
    ],
  },
  {
    no: "02",
    title: "Frameworks & Libraries",
    skills: [
      { icon: <FaReact />, name: "React", tag: "Framework" },
      { icon: <SiVuedotjs />, name: "Vue", tag: "Framework" },
      { icon: <SiNextdotjs />, name: "Next.js", tag: "Framework" },
      { icon: <SiRedux />, name: "Redux", tag: "State" },
      { icon: <SiDotnet />, name: ".NET", tag: "Framework" },
    ],
  },
  {
    no: "03",
    title: "Styling & Tooling",
    skills: [
      { icon: <SiTailwindcss />, name: "Tailwind", tag: "Styling" },
      { icon: <FaHtml5 />, name: "HTML 5 / CSS", tag: "Markup" },
      { icon: <FaGitAlt />, name: "Version Control", tag: "Tooling" },
    ],
  },
];

function SkillGroupCard({ group, index }) {
  return (
    <Reveal delay={index * 0.12} className="h-full">
      <div className="bg-ink h-full flex flex-col">
        {/* group header */}
        <div className="flex items-baseline justify-between px-6 py-5 border-b border-paper/10">
          <div className="flex items-baseline gap-3">
            <span className="font-mono text-[11px] text-acid">{group.no}</span>
            <h3 className="display text-2xl xl:text-3xl text-paper leading-none">
              {group.title}
            </h3>
          </div>
          <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-paper-dim">
            {String(group.skills.length).padStart(2, "0")}
          </span>
        </div>

        {/* rows */}
        <div className="flex flex-col flex-1">
          {group.skills.map((s) => (
            <div
              key={s.name}
              data-cursor
              className="group/row flex items-center justify-between px-6 py-4 border-b border-paper/5 last:border-b-0 hover:bg-ink-soft transition-colors duration-300"
            >
              <div className="flex items-center gap-4">
                <span className="text-xl text-paper-dim group-hover/row:text-acid group-hover/row:scale-110 transition-all duration-300">
                  {s.icon}
                </span>
                <span className="text-base xl:text-lg text-paper group-hover/row:translate-x-1 transition-transform duration-300 ease-expo">
                  {s.name}
                </span>
              </div>
              <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-paper-dim opacity-0 -translate-x-2 group-hover/row:opacity-100 group-hover/row:translate-x-0 transition-all duration-300">
                {s.tag}
              </span>
            </div>
          ))}
        </div>

        {/* card footer — keeps the grid rhythm when row counts differ */}
        <div className="mt-auto px-6 py-3 border-t border-paper/5 flex items-center justify-between font-mono text-[9px] uppercase tracking-[0.25em] text-paper-dim/60">
          <span>{group.no} / {String(SKILL_GROUPS.length).padStart(2, "0")}</span>
          <span className="w-8 h-px bg-paper/10" />
        </div>
      </div>
    </Reveal>
  );
}

function SkillMatrix({ columns }) {
  return (
    <div
      className={`grid gap-px bg-paper/10 border border-paper/10 ${
        columns === 3 ? "grid-cols-3" : "grid-cols-1 mdl:grid-cols-3"
      }`}
    >
      {SKILL_GROUPS.map((g, i) => (
        <SkillGroupCard key={g.no} group={g} index={i} />
      ))}
    </div>
  );
}

function TechMarquee() {
  return (
    <Marquee
      duration={26}
      separator=""
      items={TECH.map((t) => (
        <span
          data-cursor
          className="group flex items-center gap-3 text-paper-dim hover:text-acid transition-colors duration-300"
        >
          <span className="text-4xl sm:text-5xl">{t.icon}</span>
          <span className="display text-2xl sm:text-3xl">{t.name}</span>
        </span>
      ))}
    />
  );
}

function SectionHeader() {
  return (
    <div className="flex items-center gap-4 mb-12">
      <span className="eyebrow">(02)</span>
      <span className="h-px w-12 bg-acid" />
      <span className="eyebrow">Capabilities</span>
    </div>
  );
}

function Intro() {
  return (
    <p className="text-base sm:text-lg leading-relaxed text-paper-dim max-w-md">
      These are the things I reach for day to day. Most of it I picked
      up by shipping actual projects, breaking a few of them, and
      figuring out what holds up once real people start using it.
    </p>
  );
}

export default function Skills({ horizontal }) {
  if (horizontal) {
    return (
      <section id="skills" className="flex h-full shrink-0">
        {/* Scene 1 — statement + tech ticker */}
        <Panel horizontal className="flex flex-col justify-center">
          <div className="px-6 sm:px-10 mb-14">
            <SectionHeader />
            <div className="grid grid-cols-[1fr_0.8fr] gap-12 items-end">
              <Parallax horizontal depth={-0.05}>
                <MaskText
                  className="display text-[6vw] leading-[1.02]"
                  lineClassName="text-paper"
                  lines={[<>The tools</>, <span className="italic text-acid">I build with.</span>]}
                />
              </Parallax>
              <Reveal delay={0.1}>
                <Intro />
              </Reveal>
            </div>
          </div>
          <div className="border-y border-paper/10 py-8">
            <TechMarquee />
          </div>
        </Panel>

        {/* Scene 2 — the capability matrix */}
        <Panel horizontal className="flex items-center px-10 xl:px-20">
          <div className="w-full">
            <div className="flex items-center gap-4 mb-8">
              <span className="eyebrow">Capability Matrix</span>
              <span className="h-px flex-1 bg-paper/10" />
              <span className="eyebrow">
                {SKILL_GROUPS.reduce((n, g) => n + g.skills.length, 0)} Skills · {SKILL_GROUPS.length} Disciplines
              </span>
            </div>
            <SkillMatrix columns={3} />
          </div>
        </Panel>
      </section>
    );
  }

  return (
    <section id="skills" className="relative py-24 sm:py-36">
      {/* tech icon marquee */}
      <div className="border-y border-paper/10 py-8 mb-20 sm:mb-28">
        <TechMarquee />
      </div>

      <div className="px-6 sm:px-10">
        <SectionHeader />

        <div className="grid lgl:grid-cols-[0.9fr_1.1fr] gap-12 mb-16">
          <MaskText
            className="display text-[9vw] mdl:text-[4.5vw] leading-[1.02]"
            lineClassName="text-paper"
            lines={[<>The tools</>, <span className="italic text-acid">I build with.</span>]}
          />
          <Reveal delay={0.1} className="self-end">
            <Intro />
          </Reveal>
        </div>

        {/* capability matrix */}
        <div className="flex items-center gap-4 mb-8">
          <span className="eyebrow">Capability Matrix</span>
          <span className="h-px flex-1 bg-paper/10" />
          <span className="eyebrow">
            {SKILL_GROUPS.reduce((n, g) => n + g.skills.length, 0)} Skills · {SKILL_GROUPS.length} Disciplines
          </span>
        </div>
        <SkillMatrix />
      </div>
    </section>
  );
}
